import { Tooltip } from "@mui/material";
import ModalBox from "components/Modal";
import { Spinner } from "components/shared/Spinner";
import { format, differenceInMinutes } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    CREALO_CONTRACT_REFUSED,
    CREALO_CONTRACT_SIGNED,
    CREALO_SIGNATORY_CANCELED,
    CREALO_SIGNATORY_REFUSED,
    CREALO_SIGNATORY_SENT,
    CREALO_SIGNATORY_SIGNED,
    DOCOON_EVENT_TYPES,
} from "config/docoonEventTypes";
import type { DocoonEvent, DocoonEventsData } from "store/agreementsStore";
import { fetchAgreementPreview, fetchDocoonEvents, resetDocoonEvents, syncDocoonEvents } from "store/agreementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFunction } from "i18next";

const ICON_CHECK = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";
const ICON_LOCK =
    "M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-9h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zM8.9 6a3.1 3.1 0 0 1 6.2 0v2H8.9z";
const ICON_DOWNLOAD = "M5 20h14v-2H5zm7-18-5.5 5.5 1.42 1.42L11 5.83V16h2V5.83l3.08 3.09L17.5 7.5z";
const ICON_SEND = "M2.01 21 23 12 2.01 3 2 10l15 2-15 2z";
const ICON_CLOSE =
    "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z";

type StepState = "done" | "todo" | "refused" | "canceled";

type Step = {
    label: string;
    iconPath: string;
    state: StepState;
    at: string;
    isGlobal?: boolean;
};

function formatAt(date: string | null | undefined): string {
    if (!date) return "";
    try {
        return format(new Date(date), "dd/MM/yyyy HH:mm");
    } catch {
        return "";
    }
}

function lastCheckLabel(docoonLastCheckedAt: string | null | undefined, t: TFunction): string {
    if (!docoonLastCheckedAt) return "";
    const mins = differenceInMinutes(new Date(), new Date(docoonLastCheckedAt));
    if (mins < 1) return t("DocoonTracking.LastCheckNow");
    if (mins < 60) return t("DocoonTracking.LastCheckMins", { mins });
    return format(new Date(docoonLastCheckedAt), "dd/MM/yyyy HH:mm");
}

function buildSteps(signataire: string, events: DocoonEvent[], docoonData: DocoonEventsData, t: TFunction): Step[] {
    const signatoryEvents = events
        .filter((e) => e.signataire === signataire)
        .sort((a, b) => {
            if (!a.eventAt) return 1;
            if (!b.eventAt) return -1;
            return new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime();
        });

    const sentEvent = signatoryEvents.find((e) => e.type === CREALO_SIGNATORY_SENT);

    const steps: Step[] = [
        {
            label: t("DocoonTracking.StepSent"),
            iconPath: ICON_SEND,
            state: "done",
            at: formatAt(sentEvent?.eventAt ?? docoonData.createdAt),
            isGlobal: true,
        },
    ];

    // Per-signer events — only show types listed in DOCOON_EVENT_TYPES; signatorySent and terminal events are handled separately
    const intermediateEvents = signatoryEvents.filter((e) => e.type && DOCOON_EVENT_TYPES[e.type]);
    intermediateEvents.forEach((e) => {
        const config = DOCOON_EVENT_TYPES[e.type!]!;
        steps.push({
            label: t(config.labelKey),
            iconPath: config.iconPath,
            state: "done",
            at: formatAt(e.eventAt),
        });
    });

    // Terminal steps
    const TERMINAL_TYPES = new Set([CREALO_SIGNATORY_SIGNED, CREALO_SIGNATORY_REFUSED, CREALO_SIGNATORY_CANCELED]);
    const terminalEvents = signatoryEvents.filter((e) => e.type && TERMINAL_TYPES.has(e.type));

    if (terminalEvents.length === 0) {
        steps.push({ label: t("DocoonTracking.StepContractSigned"), iconPath: ICON_LOCK, state: "todo", at: "" });
    } else {
        for (const e of terminalEvents) {
            if (e.type === CREALO_SIGNATORY_SIGNED) {
                steps.push({
                    label: t("DocoonTracking.StepContractSigned"),
                    iconPath: ICON_CHECK,
                    state: "done",
                    at: formatAt(e.eventAt),
                });
            } else if (e.type === CREALO_SIGNATORY_REFUSED) {
                steps.push({
                    label: t("DocoonTracking.StepRefused"),
                    iconPath: ICON_CLOSE,
                    state: "refused",
                    at: formatAt(e.eventAt),
                });
            } else if (e.type === CREALO_SIGNATORY_CANCELED) {
                steps.push({
                    label: t("DocoonTracking.StepCanceled"),
                    iconPath: ICON_CLOSE,
                    state: "canceled",
                    at: formatAt(e.eventAt),
                });
            }
        }
    }

    // Global end step
    steps.push({
        label: t("DocoonTracking.StepDownload"),
        iconPath: ICON_DOWNLOAD,
        state: docoonData.downloadStatus === "downloaded" ? "done" : "todo",
        at: "",
        isGlobal: true,
    });

    return steps;
}

function StepIcon({ path, color }: { path: string; color: string }) {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
            <path d={path} />
        </svg>
    );
}

interface DocoonTrackingModalProps {
    open: boolean;
    agreementId: number | null;
    agreementTitle: string;
    agreementFilename: string;
    onClose: (hasTerminalStatus?: boolean) => void;
}

const DocoonTrackingModal = ({
    open,
    agreementId,
    agreementTitle,
    agreementFilename,
    onClose,
}: DocoonTrackingModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {
        docoonEvents: storeDocoonEvents,
        docoonEventsFetchStatus,
        docoonEventsSyncStatus,
    } = useAppSelector((s) => s.agreements);

    const [activeSignataire, setActiveSignataire] = useState<string | null>(null);
    const docoonEvents = storeDocoonEvents;

    useEffect(() => {
        if (!open || !agreementId) return;
        dispatch(fetchDocoonEvents({ agreementId }));
    }, [open, agreementId]);

    useEffect(() => {
        if (!open) {
            dispatch(resetDocoonEvents());
            setActiveSignataire(null);
        }
    }, [open]);

    const informed = docoonEvents?.informed ?? [];

    const signataires = useMemo<string[]>(() => {
        if (!docoonEvents) return [];
        const informedNames = new Set(docoonEvents.informed ?? []);
        const set = new Set<string>();
        for (const e of docoonEvents.events) {
            if (e.signataire && !informedNames.has(e.signataire)) set.add(e.signataire);
        }
        return Array.from(set);
    }, [docoonEvents]);

    // Docoon mails an informed person once the signers are done: that mail is the notification
    const informedNotifiedAt = (name: string) =>
        docoonEvents?.events
            .filter((e) => e.signataire === name && e.type === "Mail" && e.eventAt)
            .map((e) => e.eventAt as string)
            .sort()
            .at(-1);

    const currentSignataire = activeSignataire ?? signataires[0] ?? null;
    const currentIdx = signataires.indexOf(currentSignataire ?? "");

    const isContractSigned = docoonEvents?.events.some((e) => e.type === CREALO_CONTRACT_SIGNED) ?? false;
    const isContractRefused = docoonEvents?.events.some((e) => e.type === CREALO_CONTRACT_REFUSED) ?? false;

    const signedCount = useMemo(() => {
        if (!docoonEvents) return 0;
        return signataires.filter((s) =>
            docoonEvents.events.some((e) => e.signataire === s && e.type === CREALO_SIGNATORY_SIGNED),
        ).length;
    }, [docoonEvents, signataires]);

    const steps = useMemo<Step[]>(() => {
        if (!docoonEvents || !currentSignataire) return [];
        return buildSteps(currentSignataire, docoonEvents.events, docoonEvents, t);
    }, [docoonEvents, currentSignataire, t]);

    const isSyncing = docoonEventsSyncStatus === "pending";
    const isLoading = docoonEventsFetchStatus === "pending";
    const isDownloaded = docoonEvents?.downloadStatus === "downloaded";
    const isRefused = docoonEvents?.downloadStatus === "refused";
    const total = signataires.length;
    const progressPct = total > 0 ? (signedCount / total) * 100 : 0;

    const handleRefresh = () => {
        if (!agreementId || isSyncing) return;
        dispatch(syncDocoonEvents({ agreementId }));
    };

    const handleDownloadSignedPdf = () => {
        if (!agreementId) return;
        dispatch(
            fetchAgreementPreview({
                agreementId,
                filename: agreementFilename || agreementTitle || t("DocoonTracking.SignedContractFilename"),
            }),
        );
    };

    const handleClose = () => {
        onClose(isContractSigned || isContractRefused);
    };

    const signatoryStatus = (s: string): "signed" | "refused" | "canceled" | "opened" | "pending" => {
        const evts = docoonEvents?.events.filter((e) => e.signataire === s) ?? [];
        if (evts.some((e) => e.type === CREALO_SIGNATORY_REFUSED)) return "refused";
        if (evts.some((e) => e.type === CREALO_SIGNATORY_CANCELED)) return "canceled";
        if (evts.some((e) => e.type === CREALO_SIGNATORY_SIGNED)) return "signed";
        if (evts.length > 1) return "opened";
        return "pending";
    };

    const STATUS_STYLE: Record<
        "signed" | "refused" | "canceled" | "opened" | "pending",
        { fg: string; bg: string; label: string }
    > = {
        signed: { fg: "#267847", bg: "#ecfaed", label: t("DocoonTracking.StatusSigned") },
        refused: { fg: "#C0392B", bg: "rgba(192,57,43,.12)", label: t("DocoonTracking.StatusRefused") },
        canceled: { fg: "#6B7280", bg: "rgba(107,114,128,.12)", label: t("DocoonTracking.StatusCanceled") },
        opened: { fg: "#AF8442", bg: "rgba(244,160,34,.4)", label: t("DocoonTracking.StatusOpened") },
        pending: { fg: "#AF8442", bg: "rgba(244,160,34,.4)", label: t("DocoonTracking.StatusPending") },
    };

    const currentStatus = currentSignataire ? signatoryStatus(currentSignataire) : "pending";
    const globalSigned = isContractSigned;

    return (
        <ModalBox
            open={open}
            onClose={handleClose}
            mainTitle={t("DocoonTracking.ModalTitle")}
            subTitle="Docoon"
            style={{ width: 660, maxWidth: "95vw", padding: 0 }}
        >
            <div className="flex flex-col">
                {isLoading ? (
                    <div className="flex items-center justify-center py-14">
                        <Spinner size={52} />
                    </div>
                ) : !docoonEvents || signataires.length === 0 ? (
                    <div className="px-7 py-8 text-sm text-gray-400">{t("DocoonTracking.NoData")}</div>
                ) : (
                    <>
                        {/* Progress bar + global status */}
                        <div className="flex items-center gap-3 px-7 pb-4">
                            <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-green-600 transition-all duration-300"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                                {t("DocoonTracking.Progress", { signed: signedCount, total })}
                            </span>
                            <span
                                className="text-xs rounded px-2 py-0.5 whitespace-nowrap"
                                style={{
                                    color: globalSigned ? "#267847" : isContractRefused ? "#C0392B" : "#AF8442",
                                    background: globalSigned
                                        ? "#ecfaed"
                                        : isContractRefused
                                          ? "rgba(192,57,43,.12)"
                                          : "rgba(244,160,34,.4)",
                                }}
                            >
                                {globalSigned
                                    ? t("DocoonTracking.GlobalStatusSigned")
                                    : isContractRefused
                                      ? t("DocoonTracking.GlobalStatusRefused")
                                      : t("DocoonTracking.GlobalStatus")}
                            </span>
                        </div>

                        {/* Contract meta */}
                        <div className="px-7 pb-3 text-xs text-gray-500">
                            {t("DocoonTracking.ModalMeta", {
                                id: docoonEvents.docoonContractId,
                                date: formatAt(docoonEvents.createdAt).split(" ")[0],
                            })}
                        </div>

                        {/* Signatory selector */}
                        <div className="px-7 pb-2">
                            <div className="flex items-center gap-2.5 px-3 py-2 border border-gray-200 rounded">
                                <span
                                    className="w-2.5 h-2.5 rounded-full flex-none"
                                    style={{ background: STATUS_STYLE[currentStatus].fg }}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                        {currentSignataire}
                                    </div>
                                </div>
                                <span
                                    className="text-xs rounded px-2 py-0.5 whitespace-nowrap flex-none"
                                    style={{
                                        color: STATUS_STYLE[currentStatus].fg,
                                        background: STATUS_STYLE[currentStatus].bg,
                                    }}
                                >
                                    {STATUS_STYLE[currentStatus].label}
                                </span>
                                {total > 1 && (
                                    <div className="flex items-center gap-0.5 flex-none border-l border-gray-200 pl-2 ml-1">
                                        <span className="text-xs text-gray-400 mr-1 whitespace-nowrap">
                                            {currentIdx + 1}/{total}
                                        </span>
                                        <div
                                            onClick={() =>
                                                setActiveSignataire(signataires[(currentIdx - 1 + total) % total])
                                            }
                                            title="Signataire précédent"
                                            className="flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer"
                                            style={{ width: 28, height: 28, borderRadius: 4, color: "#646464" }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                            </svg>
                                        </div>
                                        <div
                                            onClick={() => setActiveSignataire(signataires[(currentIdx + 1) % total])}
                                            title="Signataire suivant"
                                            className="flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer"
                                            style={{ width: 28, height: 28, borderRadius: 4, color: "#646464" }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Timeline */}
                        <div className="px-7 py-2 max-h-80 overflow-y-auto">
                            {steps.map((step, i) => {
                                const done = step.state === "done";
                                const todo = step.state === "todo";
                                const refused = step.state === "refused";
                                const canceled = step.state === "canceled";
                                const ring = done ? "#267847" : refused ? "#C0392B" : canceled ? "#6B7280" : "#d9d9d9";
                                const iconColor = done
                                    ? "#267847"
                                    : refused
                                      ? "#C0392B"
                                      : canceled
                                        ? "#6B7280"
                                        : "#B4B4B4";
                                const isLast = i === steps.length - 1;

                                return (
                                    <div key={i} className="grid gap-x-3.5" style={{ gridTemplateColumns: "28px 1fr" }}>
                                        {/* Bullet + line */}
                                        <div className="flex flex-col items-center">
                                            <div
                                                className="w-7 h-7 rounded-full flex-none flex items-center justify-center"
                                                style={{
                                                    background: done
                                                        ? "#ecfaed"
                                                        : refused
                                                          ? "rgba(192,57,43,.08)"
                                                          : canceled
                                                            ? "rgba(107,114,128,.08)"
                                                            : "#fff",
                                                    border: `1.5px ${todo ? "dashed" : "solid"} ${ring}`,
                                                }}
                                            >
                                                <StepIcon path={done ? ICON_CHECK : step.iconPath} color={iconColor} />
                                            </div>
                                            {!isLast && (
                                                <div
                                                    className="flex-1 w-0 min-h-4"
                                                    style={{
                                                        borderLeft: `1.5px ${
                                                            done ? "solid #267847" : "dashed #eaeaea"
                                                        }`,
                                                    }}
                                                />
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className={isLast ? "pb-0" : "pb-4"}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    className="text-sm leading-snug"
                                                    style={{
                                                        fontWeight: done || refused || canceled ? 600 : 400,
                                                        color: refused
                                                            ? "#C0392B"
                                                            : canceled
                                                              ? "#6B7280"
                                                              : todo
                                                                ? "#888888"
                                                                : "#141516",
                                                    }}
                                                >
                                                    {step.label}
                                                </span>
                                                {isLast && done && (
                                                    <Tooltip
                                                        title={t("DocoonTracking.DownloadSignedPdf")}
                                                        placement="top"
                                                    >
                                                        <button
                                                            onClick={handleDownloadSignedPdf}
                                                            className="flex items-center justify-center rounded border-none cursor-pointer hover:bg-green-50"
                                                            style={{
                                                                background: "transparent",
                                                                padding: 2,
                                                                color: "#267847",
                                                            }}
                                                        >
                                                            <svg
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                            >
                                                                <path d={ICON_DOWNLOAD} />
                                                            </svg>
                                                        </button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {(step.at || todo) && (
                                                <span
                                                    className="text-xs"
                                                    style={{
                                                        color: refused ? "#C0392B" : done ? "#267847" : "#888888",
                                                    }}
                                                >
                                                    {step.at || t("DocoonTracking.StepTodo")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Informed people: notified once the contract is signed, not part of the signers */}
                        {informed.length > 0 && (
                            <div className="px-7 pt-2 pb-3 flex flex-col gap-1.5">
                                <span className="text-xs font-medium text-gray-500">
                                    {t("DocoonTracking.Informed")}
                                </span>
                                {/* About 4 rows, then scrolls: the timeline above has its own cap */}
                                <div className="flex flex-col gap-1.5 max-h-28 overflow-y-auto">
                                    {informed.map((name) => {
                                        const notifiedAt = informedNotifiedAt(name);
                                        return (
                                            <div key={name} className="flex items-center justify-between gap-3 text-sm">
                                                <span className="text-gray-900 truncate">{name}</span>
                                                <span
                                                    className="text-xs whitespace-nowrap"
                                                    style={{ color: notifiedAt ? "#267847" : "#888888" }}
                                                >
                                                    {notifiedAt
                                                        ? t("DocoonTracking.InformedNotifiedAt", {
                                                              date: formatAt(notifiedAt),
                                                          })
                                                        : t("DocoonTracking.InformedPending")}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Last check + refresh — always visible when not loading */}
                {!isLoading && (
                    <div className="mx-7 mb-4 px-3.5 py-3 bg-gray-50 border border-gray-200 rounded flex items-center justify-between gap-4">
                        <div>
                            <div className="text-xs font-semibold text-gray-900">
                                {isSyncing
                                    ? `${t("DocoonTracking.LastCheckPrefix")} ${t("DocoonTracking.Refreshing")}`
                                    : docoonEvents?.docoonLastCheckedAt
                                      ? `${t("DocoonTracking.LastCheckPrefix")} ${lastCheckLabel(
                                            docoonEvents.docoonLastCheckedAt,
                                            t,
                                        )}`
                                      : t("DocoonTracking.NeverChecked")}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{t("DocoonTracking.LastCheckHint")}</div>
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={isSyncing || isDownloaded || isRefused || isContractRefused}
                            className="inline-flex items-center gap-2 text-xs rounded px-3.5 py-1.5 border-none whitespace-nowrap cursor-pointer disabled:cursor-default"
                            style={{
                                background:
                                    isSyncing || isDownloaded || isRefused || isContractRefused ? "#eaeaea" : "#303030",
                                color:
                                    isSyncing || isDownloaded || isRefused || isContractRefused
                                        ? "rgba(0,0,0,.4)"
                                        : "#fff",
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                style={isSyncing ? { animation: "spin 0.9s linear infinite" } : undefined}
                            >
                                <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4z" />
                            </svg>
                            {isSyncing ? t("DocoonTracking.Refreshing") : t("DocoonTracking.RefreshBtn")}
                        </button>
                    </div>
                )}

                {/* Footer */}
                <div className="px-7 py-3.5 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={handleClose}
                        className="text-sm rounded px-3.5 py-1.5 bg-gray-800 text-white border-none cursor-pointer"
                    >
                        {t("DocoonTracking.Close")}
                    </button>
                </div>
            </div>
        </ModalBox>
    );
};

export default DocoonTrackingModal;
